import { initProductGalleries } from "./product-gallery";
import {
  initProductQtySelectors,
  initProductPickupSelectors,
} from "./product-qty";

const CUSTOMER_COOKIE = "sauer_customer";
const CUSTOMER_FIELDS = ["first_name", "last_name", "email", "phone"];
const COOKIE_MAX_AGE = 15552000; // 180 days

function saveCustomerCookie(form) {
  const data = {};
  for (const name of CUSTOMER_FIELDS) {
    data[name] = form.elements[name]?.value ?? "";
  }
  const encoded = encodeURIComponent(JSON.stringify(data));
  document.cookie = `${CUSTOMER_COOKIE}=${encoded}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
}

function loadCustomerCookie() {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(CUSTOMER_COOKIE + "="));
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match.split("=").slice(1).join("=")));
  } catch {
    return null;
  }
}

function prefillCheckoutForm(container) {
  const saved = loadCustomerCookie();
  if (!saved) return;
  const form = container.querySelector("[data-checkout-form]");
  if (!form) return;

  // Delay to let browser autofill run first
  setTimeout(() => {
    for (const name of CUSTOMER_FIELDS) {
      const input = form.elements[name];
      if (input && input.value === "" && saved[name]) {
        input.value = saved[name];
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }
  }, 120);
}

const overlay = document.querySelector("[data-product-overlay]");
const panel = document.querySelector("[data-product-panel]");
const panelBody = document.querySelector("[data-product-panel-body]");
const closeButton = document.querySelector("[data-product-close]");
const cartPanel = document.querySelector("[data-cart-panel]");
const cartPanelBody = document.querySelector("[data-cart-panel-body]");
const cartCloseButton = document.querySelector("[data-cart-close]");
const successPanel = document.querySelector("[data-success-panel]");
const successPanelBody = document.querySelector("[data-success-panel-body]");
const successCloseButton = document.querySelector("[data-success-close]");
const failedPanel = document.querySelector("[data-failed-panel]");
const failedPanelBody = document.querySelector("[data-failed-panel-body]");
const failedCloseButton = document.querySelector("[data-failed-close]");
const detailContainer = document.querySelector("[data-product-details]");
const openButtons = Array.from(
  document.querySelectorAll("[data-product-open]"),
);
const cartOpenButtons = Array.from(
  document.querySelectorAll("[data-cart-open]"),
);
const imageOpenButtons = Array.from(
  document.querySelectorAll("[data-product-image-open]"),
);
const cartCountElements = Array.from(
  document.querySelectorAll("[data-cart-count]"),
);

const updateCartCount = () => {
  // Sum up quantities from cart panel inputs
  const qtyInputs =
    cartPanelBody?.querySelectorAll('input[name^="items["]') ?? [];
  let count = 0;
  qtyInputs.forEach((input) => {
    count += parseInt(input.value, 10) || 0;
  });
  cartCountElements.forEach((el) => {
    el.textContent = count;
    el.classList.toggle("hidden", count === 0);
  });
};

if (overlay && panel && panelBody && detailContainer) {
  // Strip virtual segments so base works for /home, /cart, /checkout/success (e.g. /sauer-bar-test/home -> /sauer-bar-test/)
  const basePath = window.location.pathname.replace(
    /\/(cart|checkout\/success|checkout\/failed|home)\/?$/,
    "/",
  );
  const baseUrl =
    window.location.origin +
    (basePath.endsWith("/") ? basePath : basePath + "/");
  const homeUrl = baseUrl;
  const cartUrl = `${baseUrl}cart`;
  const successUrl = `${baseUrl}checkout/success`;
  const cartPanelUrl = `${baseUrl}cart/panel`;
  const successPanelUrl = `${baseUrl}checkout/success/panel`;
  let activePanel = null;

  const setOverlayState = (isOpen) => {
    overlay.classList.toggle("opacity-0", !isOpen);
    overlay.classList.toggle("pointer-events-none", !isOpen);
    overlay.setAttribute("aria-hidden", String(!isOpen));
    document.body.style.overflow = isOpen ? "hidden" : "";
  };

  const setPanelState = (target, isOpen) => {
    if (!target) return;
    target.classList.toggle("translate-x-full", !isOpen);
    target.setAttribute("aria-hidden", String(!isOpen));
  };

  const renderProduct = (slug) => {
    const detail = detailContainer.querySelector(
      `[data-product-content="${slug}"]`,
    );
    if (!detail) return false;
    panelBody.innerHTML = detail.innerHTML;
    panelBody.querySelectorAll("[data-product-gallery]").forEach((gallery) => {
      gallery.removeAttribute("data-gallery-bound");
      gallery.dataset.galleryIndex = "0";
    });
    panelBody.querySelectorAll("[data-qty-options]").forEach((group) => {
      group.removeAttribute("data-qty-bound");
    });
    panelBody.querySelectorAll("[data-pickup-options]").forEach((group) => {
      group.removeAttribute("data-pickup-bound");
    });
    initProductGalleries(panelBody);
    initProductQtySelectors(panelBody);
    initProductPickupSelectors(panelBody);
    return true;
  };

  const openProduct = (slug, url, push = true) => {
    if (!renderProduct(slug)) return;
    imageOpenButtons.forEach((btn) => btn.classList.remove("opacity-0"));

    // Deactivate keeping the text visible when product aside comes in
    // const activeImage = imageOpenButtons.find(
    //   (btn) => btn.getAttribute("data-product-slug") === slug
    // );
    // activeImage?.classList.add("opacity-0");

    setPanelState(cartPanel, false);
    setPanelState(successPanel, false);
    setPanelState(failedPanel, false);
    setPanelState(panel, true);
    setOverlayState(true);
    activePanel = "product";
    if (push) {
      history.pushState({ product: slug }, "", url || homeUrl);
    } else {
      history.replaceState({ product: slug }, "", url || homeUrl);
    }
  };

  const closeAllPanels = (push = true) => {
    setPanelState(panel, false);
    setPanelState(cartPanel, false);
    setPanelState(successPanel, false);
    setPanelState(failedPanel, false);
    setOverlayState(false);
    activePanel = null;
    imageOpenButtons.forEach((btn) => btn.classList.remove("opacity-0"));
    if (push) {
      history.pushState({ product: null }, "", homeUrl);
    } else {
      history.replaceState({ product: null }, "", homeUrl);
    }
    updateCartCount();
  };

  const renderCartPanel = async () => {
    if (!cartPanelBody) return;
    const response = await fetch(cartPanelUrl, {
      credentials: "same-origin",
    });
    if (response.ok) {
      cartPanelBody.innerHTML = await response.text();
      updateCartCount();
      prefillCheckoutForm(cartPanelBody);
    }
  };

  const renderSuccessPanel = async () => {
    if (!successPanelBody) return;
    if (successPanelBody.innerHTML.trim() !== "") return;
    const response = await fetch(successPanelUrl, {
      credentials: "same-origin",
    });
    if (response.ok) {
      successPanelBody.innerHTML = await response.text();
    }
  };

  const openCart = async (push = true) => {
    await renderCartPanel();
    setPanelState(panel, false);
    setPanelState(successPanel, false);
    setPanelState(failedPanel, false);
    setPanelState(cartPanel, true);
    setOverlayState(true);
    activePanel = "cart";
    if (push) {
      history.pushState({ cart: true }, "", cartUrl);
    } else {
      history.replaceState({ cart: true }, "", cartUrl);
    }
  };

  const openSuccess = async (push = true) => {
    await renderSuccessPanel();
    setPanelState(panel, false);
    setPanelState(cartPanel, false);
    setPanelState(successPanel, true);
    setPanelState(failedPanel, false);
    setOverlayState(true);
    activePanel = "success";
    if (push) {
      history.pushState({ success: true }, "", successUrl);
    } else {
      history.replaceState({ success: true }, "", successUrl);
    }
  };

  const openFailed = (push = true) => {
    setPanelState(panel, false);
    setPanelState(cartPanel, false);
    setPanelState(successPanel, false);
    setPanelState(failedPanel, true);
    setOverlayState(true);
    activePanel = "failed";
    if (push) {
      history.pushState({ failed: true }, "", homeUrl);
    } else {
      history.replaceState({ failed: true }, "", homeUrl);
    }
  };

  // Track if last interaction was touch
  let lastTouchTime = 0;
  document.addEventListener(
    "touchstart",
    () => {
      lastTouchTime = Date.now();
    },
    { passive: true },
  );

  const wasRecentTouch = () => Date.now() - lastTouchTime < 500;

  openButtons.forEach((btn) => {
    btn.addEventListener("click", (event) => {
      event.preventDefault();

      const isImageLink = btn.hasAttribute("data-product-image-open");
      const isTouch = wasRecentTouch();

      // On touch, image links should do nothing (just show text behind)
      if (isTouch && isImageLink) {
        return;
      }

      const slug = btn.getAttribute("data-product-slug");
      if (!slug) return;
      openProduct(slug, btn.getAttribute("href"));
    });
  });

  cartOpenButtons.forEach((btn) => {
    btn.addEventListener("click", (event) => {
      event.preventDefault();
      openCart();
    });
  });

  overlay.addEventListener("click", () => closeAllPanels());
  closeButton?.addEventListener("click", () => closeAllPanels());
  cartCloseButton?.addEventListener("click", () => closeAllPanels());
  successCloseButton?.addEventListener("click", () => closeAllPanels());
  failedCloseButton?.addEventListener("click", () => closeAllPanels());

  panelBody.addEventListener("submit", async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.querySelector("[data-open-cart]")) return;
    event.preventDefault();
    const response = await fetch(form.action, {
      method: form.method || "POST",
      body: new FormData(form),
      credentials: "same-origin",
    });
    if (!response.ok) return;
    await openCart();
  });

  cartPanelBody?.addEventListener("submit", async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.matches("[data-checkout-form]")) {
      if (!form.checkValidity()) {
        event.preventDefault();
        form.dataset.submitted = "true";
        return;
      }
      event.preventDefault();
      saveCustomerCookie(form);

      // Disable submit button to prevent double-clicks
      const submitBtn = form.querySelector('button[type="submit"]');
      const originalBtnText = submitBtn?.textContent;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = "Weiterleitung...";
      }

      try {
        const response = await fetch(form.action, {
          method: form.method || "POST",
          body: new FormData(form),
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
          },
        });

        const data = await response.json();

        if (data.redirect) {
          // Redirect to Payrexx checkout (external URL) or success page
          window.location.href = data.redirect;
          return;
        }

        if (!response.ok) {
          console.error("Checkout error:", data.error);
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalBtnText;
          }
          return;
        }

        // Fallback: if no redirect, show success panel
        cartPanelBody.innerHTML = "";
        await openSuccess();
      } catch (err) {
        console.error("Checkout fetch error:", err);
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalBtnText;
        }
      }
      return;
    }

    event.preventDefault();
    const response = await fetch(form.action, {
      method: form.method || "POST",
      body: new FormData(form),
      credentials: "same-origin",
    });
    if (!response.ok) return;
    await renderCartPanel();
  });

  cartPanelBody?.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const link = target.closest("[data-product-open]");
    if (!(link instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    const slug = link.getAttribute("data-product-slug");
    if (!slug) return;
    openProduct(slug, link.getAttribute("href"));
  });

  cartPanelBody?.addEventListener("change", async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name && target.name.startsWith("items[")) {
      if (target.value === "") return;
      const form = target.closest("form");
      if (!form) return;
      const response = await fetch(form.action, {
        method: form.method || "POST",
        body: new FormData(form),
        credentials: "same-origin",
      });
      if (!response.ok) return;
      await renderCartPanel();
    }
  });

  const qtyInputTimers = new WeakMap();
  cartPanelBody?.addEventListener("input", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.name || !target.name.startsWith("items[")) return;
    if (target.value === "") return;
    const form = target.closest("form");
    if (!form) return;
    const prevTimer = qtyInputTimers.get(target);
    if (prevTimer) clearTimeout(prevTimer);
    const timer = setTimeout(async () => {
      const response = await fetch(form.action, {
        method: form.method || "POST",
        body: new FormData(form),
        credentials: "same-origin",
      });
      if (!response.ok) return;
      if (document.activeElement !== target) {
        await renderCartPanel();
      }
    }, 300);
    qtyInputTimers.set(target, timer);
  });

  window.addEventListener("popstate", (event) => {
    const slug = event.state?.product;
    const cart = event.state?.cart;
    const success = event.state?.success;
    const failed = event.state?.failed;
    if (slug) {
      openProduct(slug, null, false);
      return;
    }
    if (cart) {
      openCart(false);
      return;
    }
    if (success) {
      openSuccess(false);
      return;
    }
    if (failed) {
      openFailed(false);
      return;
    }
    closeAllPanels(false);
  });

  const params = new URLSearchParams(window.location.search);
  const initialSlug = params.get("product");
  if (initialSlug) {
    openProduct(initialSlug, window.location.href, false);
  }

  if (/\/cart\/?$/.test(window.location.pathname)) {
    openCart(false);
  }

  // Return from Payrexx: same page as Warenkorb, open existing success aside
  if (new URLSearchParams(window.location.search).get("success") === "1") {
    openSuccess(false);
  }

  // Return from failed payment: open failed aside
  if (new URLSearchParams(window.location.search).get("failed") === "1") {
    openFailed(false);
  }
}
